import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FieldModel } from 'app/models/shared/field.model';
import { FieldGroupModel } from 'app/models/shared/fieldGroup.model';
import { TabModel } from 'app/models/shared/tab.model';
import { UserService } from 'app/services/user.service';
import { EditorPageComponent } from '../editor-page/editor-page.component';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { AlertController, PopoverController, ActionSheetController, LoadingController, ModalController, Platform } from '@ionic/angular';
import { ContactService } from 'app/services/contact.service';
import { GeneralService } from 'app/services/general.service';
import { OpportunityService } from 'app/services/opportunity.service';
import { StorageService } from 'app/services/storage.service';
import { VehicleService } from 'app/services/vehicle.service';
import { ListItemModel } from 'app/models/shared/listItem.model';
import { PriceEditorMode } from 'app/models/shared/priceEditor.model';
import { forkJoin } from 'rxjs';

@Component({
    selector: 'app-price-editor',
    templateUrl: './price-editor.component.html',
    styleUrls: ['./price-editor.component.scss'],
    standalone: false
})
export class PriceEditorComponent extends EditorPageComponent implements OnInit
{

    @Input() mode: PriceEditorMode = 'simple';

    @Input() title: string;

    //simple mode
    @Input() price: number = 0;
    @Input() taxType: string = '';
    @Input() removeTaxTypes: boolean = false;

    //extended mode (includes price and tax type)
    @Input() taxRate: string = '';
    @Input() taxAmount: number = 0;
    @Input() currency: string = '';
    @Input() editableCurrency: boolean = false;
    @Input() editableTaxRate: boolean = true;

    public isDirty: boolean = false;
    public skeleton: boolean = true;
    public activeSegment: string = "";

    public originalTaxAmount: number = 0;
    public field_groups: FieldGroupModel[] = [];
    public taxStatuses: ListItemModel[] = [];
    public taxRates: ListItemModel[] = [];
    public currencies: ListItemModel[] = [];
    public isTaxAmountEditable: boolean = false;

    constructor(
        public contactService: ContactService,
        public generalService: GeneralService,
        public userService: UserService,
        public opportunityService: OpportunityService,
        public route: ActivatedRoute,
        public titleService: Title,
        public storage: StorageService,
        public alertController: AlertController,
        public popoverController: PopoverController,
        public actionSheetController: ActionSheetController,
        public loadingController: LoadingController,
        protected cd: ChangeDetectorRef,
        public modalController: ModalController,
        public platform: Platform,
        public vehicleService: VehicleService
    )
    {
        super(userService, route, titleService, storage, alertController, popoverController, actionSheetController, loadingController, cd, platform);
    }

    ngOnInit()
    {
        this.taxAmount = this.taxAmount ? parseFloat(this.taxAmount.toString()) : 0;
        this.originalTaxAmount = this.taxAmount;

        this.generalService.getCachedTaxStatuses(false).subscribe(w =>
        {
            this.taxType = this.taxType.toString();
            this.taxStatuses = w.map(v => new ListItemModel(v.taxStatusEnum.toString(), v.name.toString(), null, null, v));
            if (this.removeTaxTypes)
            {
                this.taxStatuses = this.taxStatuses.filter(x => x.id != "2010" && x.id != "2011" && x.id != "2012");
            }
            var taxStatus = this.taxStatuses.find(x => x.id == this.taxType || x.name == this.taxType || x.sideload.reference.toLowerCase() == this.taxType.toLowerCase());
            if (taxStatus)
            {
                this.taxType = taxStatus.id;
            }

            if (this.mode == 'extended')
            {
                forkJoin({
                    rates: this.generalService.getCachedTaxRates(),
                    baseCurrency: this.generalService.getCachedBaseCurrency(),
                    currencies: this.generalService.getCachedCurrencies()
                })
                .subscribe(({ rates, baseCurrency, currencies }) => {
                    this.taxRates = [];
                    this.taxRates.push(new ListItemModel(rates.baseRate.toString(), `${rates.baseRate}%`, null, null, rates));
                    if (rates.previousRate != null && rates.previousRate != undefined && rates.previousRate != 0)
                    {
                        this.taxRates.push(new ListItemModel(rates.previousRate.toString(), `${rates.previousRate}%`, null, null, rates));
                    }
                    if (this.taxRate == "")
                    {
                        this.taxRate = rates.baseRate.toString();
                    }

                    if (this.currency == "")
                    {
                        this.currency = baseCurrency.currencyId;
                    }

                    this.currencies = currencies.map(v => new ListItemModel(v.currencyId, v.reference, null, null, v));

                    this.setupFieldGroups();
                });
            }
            else
            {
                this.setupFieldGroups();
            }
        });
    }

    public fieldGroupTabs: TabModel[];

    setupFieldGroups()
    {
        //The alternative is to call the business rules for CalculateTax
        this.taxAmount = this.calculateTax(this.taxType, this.price, parseFloat(this.taxRate), this.taxAmount, 2);
        this.fieldGroupTabs = [];
        var field_groups = this.getFieldGroups(this.mode);
        this.fieldGroupTabs.push(new TabModel("Editor", '', field_groups, false, "editor", "", true, true));
        this.skeleton = false;
    }

    async presentAlert(title: string, message: string, back: boolean = false)
    {
        this.userService.presentAlert(title, message, back);
    }

    public getFieldGroups(mode: PriceEditorMode): FieldGroupModel[]
    {

        var field_groups = [];
        var group;
        switch (mode)
        {

            case 'simple':
                group = new FieldGroupModel("");

                var amount = new FieldModel('Amount', this.price, 'currency', "price", true, false, null, "price-editor-amount");
                amount.parent = 'taxType';
                amount.inverseCheck = true;
                amount.toggleEditableWhen = '2010|2011|2012';
                amount.editable = (amount.toggleEditableWhen.indexOf(this.taxType.toString()) == -1);
                group.addField(amount);

                var taxType = new FieldModel('Tax type', this.taxType, 'select', 'taxType', true, false, this.taxStatuses);
                taxType.hasChildren = true;
                group.addField(taxType);
                field_groups.push(group);

                break;
            case 'extended':
                group = new FieldGroupModel("");

                if (this.editableCurrency)
                {
                    group.addField(new FieldModel("Currency", this.currency, "select", "currency", true, false, this.currencies));
                }
                else
                {
                    //get the currency name from the currencies array
                    var currency = this.currencies.find(v => v.sideload.currencyId == this.currency);
                    var currencyName = this.currencies.find(v => v.sideload.currencyId == this.currency) ? currency.name : this.currency;
                    group.addField(new FieldModel("Currency", currencyName, "text", "currency", false, false, this.currencies, null, null, true));
                }

                var amount = new FieldModel('Amount', this.price, 'currency', "price", true, false, null, "price-editor-amount");
                amount.parent = 'taxType';
                amount.inverseCheck = true;
                amount.toggleEditableWhen = '2010|2011|2012';
                amount.editable = (amount.toggleEditableWhen.indexOf(this.taxType.toString()) == -1);
                amount.hasChildren = true;
                amount.detailClass = "";
                group.addField(amount);

                var taxType = new FieldModel('Tax type', this.taxType, 'select', 'taxType', true, false, this.taxStatuses);
                taxType.hasChildren = true;
                group.addField(taxType);

                var taxAmount = new FieldModel("Tax amount", this.taxAmount, "currency", "taxAmount", true, true, null, null, null, true);
                taxAmount.parent = 'price';
                taxAmount.editable = this.taxType == "1030" || this.taxType == "1031";
                group.addField(taxAmount);

                group.addField(new FieldModel("Tax rate", this.taxRate, "select", "taxRate", this.editableTaxRate, false, this.taxRates, null, null, true));
                field_groups.push(group);
                break;
        }
        return field_groups;
    }



    async cancelModal()
    {
        if (this.isDirty)
        {
            //prompt user to save
            const alert = await this.alertController.create({
                header: "Unsaved changes",
                message: "Some data has changed. Do you want to close without saving?",
                buttons: [
                    {
                        text: 'Cancel',
                        role: 'cancel',
                        cssClass: 'primary',
                        handler: () =>
                        {
                            //do nothing
                            return;
                        }
                    },
                    {
                        text: 'OK',
                        cssClass: 'danger',
                        handler: () =>
                        {
                            this.dismissTheModal(false);
                        }
                    }
                ]
            });

            await alert.present();
        }
        else
        {
            this.dismissTheModal(false);
        }
    }

    async dismissTheModal(save: boolean)
    {
        if (save)
        {

            //find the taxStatus in taxStatuses
            var taxStatus = this.taxStatuses.find(v => v.id == this.taxType);
            // return the data object
            this.modalController.dismiss({
                'result': {
                    price: this.price,
                    taxType: taxStatus,
                    taxRate: this.taxRate,
                    taxAmount: this.taxAmount,
                    currency: this.currency,
                    save: save
                }
            });
        }
        else
        {
            // Dismiss the top modal returning some data object
            this.modalController.dismiss({
                'result': {
                    save: save
                }
            });
        }
    }

    handleFieldChangedAdditional(ev)
    {
        if (!(ev.detail)) return;

        this.isDirty = true;

        this.handleFieldChangedForTabs(ev, this.fieldGroupTabs);

        switch (ev.detail.id)
        {
            case "price":
                this.price = ev.detail.value;
                this.taxAmount = this.calculateTax(this.taxType, this.price, parseFloat(this.taxRate), this.taxAmount, 2);
                break;
            case "currency":
                this.currency = ev.detail.value;
                break;
            case "taxType":
                this.taxType = ev.detail.value;
                this.isTaxAmountEditable = ev.detail.value == "1030" || ev.detail.value == "1031";
                this.taxAmount = this.calculateTax(this.taxType, this.price, parseFloat(this.taxRate), this.taxAmount, 2);
                this.setupFieldGroups();
                break;
            case "taxAmount":
                this.taxAmount = ev.detail.value;
                break;
            case "taxRate":
                this.taxRate = ev.detail.value;
                this.taxAmount = this.calculateTax(this.taxType, this.price, parseFloat(this.taxRate), this.taxAmount, 2);
                this.setupFieldGroups();
                break;
        }

        this.cd.detectChanges();
    }

    calculateTax(taxStatusEnum: string, amount: number, taxRate: number, taxOverrideAmount: number, roundingDecimalPlaces: number)
    {
        var result = 0;
        switch (taxStatusEnum)
        {
            case '1010':
                var taxRateFactor = taxRate / 100;
                if (taxRateFactor == 0)
                {
                    result = 0;
                }
                else
                {
                    result = amount / (1 + (1 / taxRateFactor));
                }
                break;
            case '1011':

                var taxRateFactor = taxRate / 100;

                result = amount * taxRateFactor;
                break;
            case '1040':
                result = amount;
                break;
            case '1030':
                result = taxOverrideAmount;
                break;
            case '1031':
                result = taxOverrideAmount;
                break;
            default:
                result = 0;
                break;
        }

        return parseFloat(result.toFixed(roundingDecimalPlaces));
    }



    public handleFieldChangedForTabs(ev:any, fieldGroupTabs:TabModel[])
    {
        // console.log("handleFieldChangedForTabs", ev);

        //see if this field has children
        var changedField: FieldModel = ev.detail.field;
        if (changedField.hasChildren)
        {
            switch (changedField.type)
            {
                case "currency":
                    this.checkCurrencyChildren(changedField, <any>ev.detail.field.detailObject, fieldGroupTabs);
                    break;
            }
        }
    }

    public checkCurrencyChildren(changedField: FieldModel, detailObject: any, fieldGroupTabs: TabModel[])
    {

        var prefix = changedField.detailClass;
        // console.log("checkAddressChildren", changedField);
        for (let field_group_tab of fieldGroupTabs)
        {
            for (let field_group of field_group_tab.fieldGroups)
            {
                for (let field_test of field_group.fields)
                {
                    if (field_test.parent == changedField.id)
                    {
                        // console.log("found a child, " + field_test.id);

                        var fieldWithoutPrefix = field_test.id.replace(prefix, '');
                        field_test.dirty = true;

                        // console.log(fieldWithoutPrefix);
                        switch (fieldWithoutPrefix)
                        {
                            case "taxAmount":
                                field_test.value = this.calculateTax(this.taxType, changedField.value, parseFloat(this.taxRate), this.taxAmount, 2);
                                field_test.valueChanged$.next(null);
                                break;
                        }

                    }
                }
            }
        }
    }

}
